// 30 oct 2026 (Augusto: "todos los primeros o segundos días del mes se
// pagan tarjetas de crédito al 100%, ¿cómo podemos programarlo?"; "yo
// siempre pago el total a pagar, no el mínimo").
//
// Configuración del pago programado de tarjetas. El día elegido, PCM deja
// en Revisión pendiente el pago del Pago total de cada tarjeta y moneda,
// desde estas cuentas, y el usuario lo confirma con un clic. PCM no mueve
// plata en el banco. Ver backend services/pago_tarjetas.py.
import React, { useEffect, useState } from 'react';
import Paper from '@mui/material/Paper';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Switch from '@mui/material/Switch';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Button from '@mui/material/Button';
import Alert from '@mui/material/Alert';
import apiClient from '../services/api';

const simbolo = (moneda) => (moneda === 'USD' ? 'US$' : 'S/');
const fmt = (n) => Number(n || 0).toLocaleString('es-PE', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const PagoAutomaticoTarjetas = ({ isSpanish = true }) => {
  const [config, setConfig] = useState(null);
  const [form, setForm] = useState(null);
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState(null);
  const [guardado, setGuardado] = useState(false);

  const aplicar = (data) => {
    setConfig(data);
    setForm({
      activo: Boolean(data.activo),
      dia: data.dia || 1,
      cuenta_pen_id: data.cuenta_pen_id || '',
      cuenta_usd_id: data.cuenta_usd_id || '',
    });
  };

  useEffect(() => {
    apiClient
      .get('/debts/config/pago-automatico')
      .then((r) => aplicar(r.data))
      .catch(() => setConfig(null));
  }, []);

  if (!config || !form || (config.tarjetas || []).length === 0) return null;

  const cuentasPEN = config.cuentas.filter((c) => c.moneda === 'PEN');
  const cuentasUSD = config.cuentas.filter((c) => c.moneda === 'USD');
  const hayUSD = config.tarjetas.some((t) => t.moneda === 'USD' && t.monto > 0);
  const cambiado =
    form.activo !== Boolean(config.activo) ||
    Number(form.dia) !== Number(config.dia) ||
    String(form.cuenta_pen_id || '') !== String(config.cuenta_pen_id || '') ||
    String(form.cuenta_usd_id || '') !== String(config.cuenta_usd_id || '');

  const guardar = async () => {
    setGuardando(true);
    setError(null);
    setGuardado(false);
    try {
      const { data } = await apiClient.put('/debts/config/pago-automatico', {
        activo: form.activo,
        dia: Number(form.dia),
        cuenta_pen_id: form.cuenta_pen_id || null,
        cuenta_usd_id: form.cuenta_usd_id || null,
      });
      aplicar(data);
      setGuardado(true);
    } catch (err) {
      setError(
        err.response?.data?.detail ||
          (isSpanish ? 'No se pudo guardar. Revisa tu conexión e inténtalo de nuevo.' : 'Could not save.')
      );
    } finally {
      setGuardando(false);
    }
  };

  const totales = config.tarjetas.reduce((acc, t) => {
    acc[t.moneda] = (acc[t.moneda] || 0) + Number(t.monto || 0);
    return acc;
  }, {});

  return (
    <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
      <FormControlLabel
        control={
          <Switch
            checked={form.activo}
            onChange={(e) => setForm((f) => ({ ...f, activo: e.target.checked }))}
          />
        }
        label={
          <Box>
            <Typography variant="body2" fontWeight={600}>
              {isSpanish ? 'Pagar mis tarjetas al 100% cada mes' : 'Pay my cards in full every month'}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {isSpanish
                ? 'El día que elijas, PCM deja listo en Revisión pendiente el pago del Pago total de cada tarjeta (no el mínimo) y te avisa por correo. Cuando pagues en tu banco, lo confirmas con un clic. PCM no mueve dinero de tus cuentas.'
                : 'On the day you choose, PCM prepares the full-balance payment of each card in Pending review and emails you. Once you pay at your bank, confirm it with one click. PCM never moves money.'}
            </Typography>
          </Box>
        }
      />

      {form.activo && (
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mt: 2, alignItems: 'center' }}>
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel id="pago-dia">{isSpanish ? 'Día del mes' : 'Day of month'}</InputLabel>
            <Select
              labelId="pago-dia"
              label={isSpanish ? 'Día del mes' : 'Day of month'}
              value={form.dia}
              onChange={(e) => setForm((f) => ({ ...f, dia: e.target.value }))}
            >
              {[1, 2, 3, 4, 5].map((d) => (
                <MenuItem key={d} value={d}>{isSpanish ? `Día ${d}` : `Day ${d}`}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 240 }}>
            <InputLabel id="pago-pen">{isSpanish ? 'Pago en soles desde' : 'Pay soles from'}</InputLabel>
            <Select
              labelId="pago-pen"
              label={isSpanish ? 'Pago en soles desde' : 'Pay soles from'}
              value={form.cuenta_pen_id}
              onChange={(e) => setForm((f) => ({ ...f, cuenta_pen_id: e.target.value }))}
            >
              <MenuItem value=""><em>{isSpanish ? 'Ninguna' : 'None'}</em></MenuItem>
              {cuentasPEN.map((c) => (
                <MenuItem key={c.id} value={c.id}>{c.nombre}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 240 }}>
            <InputLabel id="pago-usd">{isSpanish ? 'Pago en dólares desde' : 'Pay dollars from'}</InputLabel>
            <Select
              labelId="pago-usd"
              label={isSpanish ? 'Pago en dólares desde' : 'Pay dollars from'}
              value={form.cuenta_usd_id}
              onChange={(e) => setForm((f) => ({ ...f, cuenta_usd_id: e.target.value }))}
            >
              <MenuItem value=""><em>{isSpanish ? 'Ninguna' : 'None'}</em></MenuItem>
              {cuentasUSD.map((c) => (
                <MenuItem key={c.id} value={c.id}>{c.nombre}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      )}

      {form.activo && hayUSD && !form.cuenta_usd_id && (
        <Alert severity="info" sx={{ mt: 2 }}>
          {isSpanish
            ? 'Tienes deuda en dólares. Sin una cuenta en dólares elegida, PCM solo preparará los pagos en soles.'
            : 'You have debt in dollars. Without a dollar account, PCM will only prepare the soles payments.'}
        </Alert>
      )}

      {form.activo && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>
          {isSpanish ? 'Si fuera hoy, se prepararía: ' : 'If it were today: '}
          {Object.entries(totales)
            .filter(([, v]) => v > 0)
            .map(([m, v]) => `${simbolo(m)} ${fmt(v)}`)
            .join(' + ') || (isSpanish ? 'nada, tus tarjetas están en cero' : 'nothing, your cards are at zero')}
          {isSpanish ? ' (Pago total de cada extracto, menos lo que ya pagaste después del corte).' : '.'}
        </Typography>
      )}

      {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
      {guardado && !cambiado && (
        <Alert severity="success" sx={{ mt: 2 }}>
          {config.activo
            ? (isSpanish ? `Listo. El día ${config.dia} de cada mes tendrás tus pagos preparados en Revisión pendiente.` : 'Saved.')
            : (isSpanish ? 'Pago programado apagado.' : 'Scheduled payment turned off.')}
        </Alert>
      )}

      {cambiado && (
        <Box sx={{ mt: 2 }}>
          <Button
            variant="contained"
            size="small"
            disabled={guardando || (form.activo && !form.cuenta_pen_id && !form.cuenta_usd_id)}
            onClick={guardar}
          >
            {guardando ? (isSpanish ? 'Guardando...' : 'Saving...') : (isSpanish ? 'Guardar' : 'Save')}
          </Button>
        </Box>
      )}
    </Paper>
  );
};

export default PagoAutomaticoTarjetas;
